import { useEffect, useState } from "react";
import InputSlider from "../../../components/ui/slider";
import REITab from "../../../components/ui/tab";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import REIToggleButton from "../../../components/ui/toggleButton";
import { IMyPortfolio } from "../../../interface/myPortfolio";
import { ITab } from "../../../interface/tab";
import CashFlowSummary from "./CashFlowDetailTabs/CashFlowSummary";
import CashFlowRent from "./CashFlowDetailTabs/CashFlowRent";
import PurchaseCost from "./CashFlowDetailTabs/PurchaseCost";
import Expense from "./CashFlowDetailTabs/Expense";
import CashFlowTaxAndDepreciation from "./CashFlowDetailTabs/CashFlowTaxAndDepreciation";
import { REIButton } from "../../../components/ui";
import Loan from "./CashFlowDetailTabs/Loan";
import { useNavigate } from "react-router-dom";
import { getCurrentFinancialYear } from "../../../utils/commonUtil";

const CashFlowDetails: React.FC<{
  portFolioData: IMyPortfolio;
  handleRefresh: () => void;
  year: number;
  onYearChange: (year: number) => void;
  isShowingTotal?: boolean;
}> = ({ portFolioData, year, onYearChange, handleRefresh, isShowingTotal = true }) => {
  const [currentYear, setCurrentYear] = useState<number>(Number(getCurrentFinancialYear()));
  const [isEditable, setIsEditable] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (portFolioData && portFolioData.CashflowInDetail && portFolioData.CashflowInDetail.CashflowSummary) {
      setCurrentYear(Number(portFolioData.CashflowInDetail.CashflowSummary.Actuals[0].Year.slice(2, 4)));
    }
  }, [portFolioData]);

  useEffect(() => {
    handleRefresh();
  }, [isEditable]);

  const tabs: ITab[] = [
    {
      header: "Summary",
      content: (
        <CashFlowSummary cashFlowData={portFolioData} year={"20" + year.toString()} isShowingTotal={isShowingTotal} />
      ),
      closable: false,
    },
    {
      header: "Rent",
      content: (
        <CashFlowRent
          cashFlowData={portFolioData}
          year={"20" + year.toString()}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          isShowingTotal={isShowingTotal}
        />
      ),
      closable: false,
    },
    {
      header: "Purchase costs",
      content: (
        <PurchaseCost
          cashFlowData={portFolioData}
          year={"20" + year.toString()}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          isShowingTotal={isShowingTotal}
        />
      ),
      closable: false,
    },
    {
      header: "Loan",
      content: (
        <Loan
          cashFlowData={portFolioData}
          year={"20" + year.toString()}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          isShowingTotal={isShowingTotal}
        />
      ),
      closable: false,
    },
    {
      header: "Expenses",
      content: (
        <Expense
          cashFlowData={portFolioData}
          year={"20" + year.toString()}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          isShowingTotal={isShowingTotal}
        />
      ),
      closable: false,
    },
    {
      header: "Tax & Depreciation",
      content: (
        <CashFlowTaxAndDepreciation
          cashFlowData={portFolioData}
          year={"20" + year.toString()}
          isEditable={isEditable}
          setIsEditable={setIsEditable}
          isShowingTotal={isShowingTotal}
        />
      ),
      closable: false,
    },
  ];

  const HeaderTemplate = (
    <div className="rounded-t-lg p-2 px-4 bg-slate-600 text-white flex gap-3 justify-around">
      <div className="flex w-1/3 content-start">
        <h3>Cash flow in detail</h3>
      </div>
      <div className="flex flex-row gap-3 align-middle w-1/3 content-center">
        <h3>Showing:</h3>
        <InputSlider
          id="mpcfd-sldr-year"
          value={year}
          onChange={(e) => {
            onYearChange(e);
          }}
          min={currentYear}
          max={currentYear + (currentYear < Number(getCurrentFinancialYear()) ? 10 : 9)}
          step={1}
          classNames="self-center w-1/2"
        />
        <h3>
          FY-{year}{" "}
          <span className="text-sm">{year.toString() == getCurrentFinancialYear() ? "(Actuals YTD)" : ""}</span>
        </h3>
      </div>
      <div className="w-1/3 content-center text-end gap-2">
        <REIButton
          id="mpcfd-btn-updateforecast"
          label="Update forecast"
          classNames="mr-2"
          icon="pi pi-pencil"
          onClick={() => {
            navigate(`/portfolio-tracker`, {
              state: {
                tab: "MyPropertyTab",
                MyPropertyId: undefined,
                AddNewProperty: false,
                EditProperty: true,
                portfolioTrackerId: portFolioData.PortfolioTrackerDetailLabel.PortfolioTrackerId,
              },
            });
          }}
          //size="small"
        ></REIButton>
        <REIToggleButton
          id="mpcfd-tgl-editable"
          index=""
          isSelected={isEditable}
          offlabel="Use wizards to update actuals"
          onToggleChange={() => {
            const currentTab = activeTab;
            setIsEditable(!isEditable);
            if (currentTab === 0) {
              setActiveTab(4);
            } else {
              setActiveTab(currentTab);
            }
          }}
          onlabel="Currently using wizard to edit actuals"
        />
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <REIToggablePanel
        contentComponent={
          <>
            <div className="mx-auto p-4 content-center justified-between-tabview">
              <REITab
                id="mpcfd-tab-cashflow"
                tabs={tabs}
                classNames="text-center"
                activeIndex={activeTab}
                onTabChange={(e) => setActiveTab(e.index)}
              ></REITab>
            </div>
          </>
        }
        defaultCollapse={false}
        header=""
        istoggleable={false}
        customHeader={HeaderTemplate}
      ></REIToggablePanel>
    </div>
  );
};

export default CashFlowDetails;
